import React, { useContext, useEffect, useRef, useState } from "react";
import { useGetRecordQuery } from "@/redux/services/recordApi";
import {
	Alert,
	Spin,
	Table,
	Button,
	Flex,
	Form,
	Input,
	Popconfirm,
} from "antd";
import { DownloadButton } from "./document";

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
	const [form] = Form.useForm();
	return (
		<Form form={form} component={false}>
			<EditableContext.Provider value={form}>
				<tr {...props} />
			</EditableContext.Provider>
		</Form>
	);
};
const EditableCell = ({
	title,
	editable,
	children,
	dataIndex,
	record,
	handleSave,
	...restProps
}) => {
	const [editing, setEditing] = useState(false);
	const inputRef = useRef(null);
	const form = useContext(EditableContext);
	useEffect(() => {
		if (editing) {
			inputRef.current?.focus();
		}
	}, [editable, editing]);
	const toggleEdit = () => {
		setEditing(!editing);
		form.setFieldsValue({
			[dataIndex]: record[dataIndex],
		});
	};
	const save = async () => {
		try {
			const values = await form.validateFields();
			toggleEdit();
			handleSave({
				...record,
				...values,
			});
		} catch (errInfo) {
			console.log("Save failed:", errInfo);
		}
	};
	let childNode = children;
	if (editable) {
		childNode = editing ? (
			<Form.Item
				style={{
					margin: 0,
				}}
				name={dataIndex}
				rules={[
					{
						required: true,
						message: `${title} is required.`,
					},
				]}
			>
				<Input ref={inputRef} onPressEnter={save} onBlur={save} />
			</Form.Item>
		) : (
			<div
				className="editable-cell-value-wrap"
				style={{
					paddingRight: 24,
				}}
				onClick={toggleEdit}
			>
				{children}
			</div>
		);
	}
	return <td {...restProps}>{childNode}</td>;
};

const EditRecordTable = ({ selectedUserId, year, month }) => {
	const [dataSource, setDataSource] = useState([]);
	const [count, setCount] = useState(2);
	const [editingKey, setEditingKey] = useState("");

	const { data, isLoading, isError } = useGetRecordQuery(
		{
			year: year,
			month: month,
			userId: selectedUserId,
		},
		{ skip: selectedUserId == null }
	);

	useEffect(() => {
		setDataSource(data?.data ?? []);
	}, [data]);

	if (isLoading) {
		return <Spin tip="Loading..." size="large"></Spin>;
	}

	if (isError) {
		return (
			<Alert message="Fail to get record" description="" type="error" />
		);
	}

	const handleDelete = (key) => {
		const newData = dataSource.filter((item) => item.key !== key);
		setDataSource(newData);
	};

	const handleAdd = () => {
		const newData = {
			key: count,
			name: `Edward King ${count}`,
			age: "32",
			address: `London, Park Lane no. ${count}`,
		};
		setDataSource([...dataSource, newData]);
		setCount(count + 1);
	};
	const handleSave = (row) => {
		const newData = [...dataSource];
		const index = newData.findIndex((item) => row.key === item.key);
		const item = newData[index];
		newData.splice(index, 1, {
			...item,
			...row,
		});
		setDataSource(newData);
	};
	const components = {
		body: {
			row: EditableRow,
			cell: EditableCell,
		},
	};

	const isEditing = (record) => record.id === editingKey;

	const edit = (key) => {
		setEditingKey(key);
	};

	const cancel = () => {
		setEditingKey("");
	};

	const save = (key) => {
		setEditingKey("");
	};

	const defaultColumns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "People number",
			dataIndex: "people_no",
			key: "people_no",
		},
		{
			title: "Product ID",
			dataIndex: "product_id",
			key: "product_id",
		},
		{
			title: "Product Title",
			dataIndex: "product_title",
			key: "product_title",
		},
		{
			title: "Total Price",
			dataIndex: "total_price",
			key: "total_price",
		},
		{
			title: "Travel Date",
			dataIndex: "travel_date",
			key: "travel_date",
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
		},
		{
			title: "operation",
			dataIndex: "operation",
			render: (_, record) => {
				const editable = isEditing(record);
				return editable ? (
					<span>
						<Button onClick={() => save(record.id)}>Save</Button>
						<Button onClick={() => cancel()}>Cancel</Button>
					</span>
				) : (
					<Button
						disabled={editingKey !== ""}
						onClick={() => edit(record.id)}
					>
						Edit
					</Button>
				);
			},
		},
		{
			title: "operation",
			dataIndex: "operation",
			render: (_, record) =>
				dataSource.length >= 1 ? (
					<Popconfirm
						title="Sure to delete?"
						onConfirm={() => handleDelete(record.id)}
					>
						<a>Delete</a>
					</Popconfirm>
				) : null,
		},
	];

	const columns = defaultColumns.map((col) => {
		return {
			...col,
			onCell: (record) => ({
				record,
				dataIndex: col.dataIndex,
				title: col.title,
				handleSave,
				editable: isEditing(record) && col.dataIndex !== "operation",
			}),
		};
	});

	return (
		<Flex vertical gap="large">
			<DownloadButton data={data} />

			<Button
				onClick={handleAdd}
				type="primary"
				style={{
					marginBottom: 16,
				}}
			>
				Add a row
			</Button>
			<Table
				components={components}
				bordered
				dataSource={dataSource}
				columns={columns}
			/>
		</Flex>
	);
};

const RecordTable = ({ selectedUserId, year, month }) => {
	const { data, isLoading, isError } = useGetRecordQuery(
		{
			year: year,
			month: month,
			userId: selectedUserId,
		},
		{ skip: selectedUserId == null }
	);

	if (isLoading) {
		return <Spin tip="Loading..." size="large"></Spin>;
	}

	if (isError) {
		return (
			<Alert message="Fail to get record" description="" type="error" />
		);
	}

	const dataSource = data?.data ?? [];
	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "People number",
			dataIndex: "people_no",
			key: "people_no",
		},
		{
			title: "Product ID",
			dataIndex: "product_id",
			key: "product_id",
		},
		{
			title: "Product Title",
			dataIndex: "product_title",
			key: "product_title",
		},
		{
			title: "Total Price",
			dataIndex: "total_price",
			key: "total_price",
		},
		{
			title: "Travel Date",
			dataIndex: "travel_date",
			key: "travel_date",
		},
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
		},
	];

	return (
		<Flex vertical gap="large">
			<DownloadButton data={data} />
			<Table dataSource={dataSource} columns={columns} />
		</Flex>
	);
};

export { RecordTable, EditRecordTable };
